/**
 * Process-wide Whisper pipeline cache.
 *
 * transformers.js `pipeline()` builds a new ONNX/WASM session every call.
 * CoachVoiceButton remounts whenever Pingflo's composer opens/closes, so
 * constructing a session per mount both re-shows the load spinner and leaks
 * WASM heaps until the tab OOMs.
 *
 * Load once per model id, keep the session for the page lifetime.
 */

import { DEFAULT_MODEL_ID, WHISPER_DEVICE, WHISPER_DTYPE } from "./ModelManager";
import { configureTransformersEnv } from "./onnxWasmPaths";

/** @typedef {{ pipeline: object|null, promise: Promise<object>|null, listeners: Set<Function> }} CacheEntry */

/** @type {Map<string, CacheEntry>} */
const cache = new Map();

let loadPipelineFn = loadTransformersPipeline;

async function loadTransformersPipeline(modelId, options) {
  const { pipeline, env } = await import("@huggingface/transformers");
  configureTransformersEnv(env, {
    origin: typeof window !== "undefined" ? window.location.origin : "",
  });
  return pipeline("automatic-speech-recognition", modelId, options);
}

/**
 * @param {string} [modelId]
 * @returns {boolean}
 */
export function isWhisperPipelineReady(modelId = DEFAULT_MODEL_ID) {
  return Boolean(cache.get(modelId)?.pipeline);
}

/**
 * @param {string} [modelId]
 * @returns {object|null}
 */
export function peekWhisperPipeline(modelId = DEFAULT_MODEL_ID) {
  return cache.get(modelId)?.pipeline || null;
}

/**
 * Load (or reuse) the Whisper pipeline for `modelId`. Concurrent callers share
 * one in-flight promise; later callers skip LOADING when `peek` is non-null.
 *
 * @param {string} [modelId]
 * @param {{ onProgress?: (ev: object) => void }} [options]
 * @returns {Promise<object>}
 */
export function loadWhisperPipeline(modelId = DEFAULT_MODEL_ID, options = {}) {
  const onProgress = options.onProgress;
  const existing = cache.get(modelId);

  if (existing?.pipeline) {
    return Promise.resolve(existing.pipeline);
  }

  if (existing?.promise) {
    if (onProgress) existing.listeners.add(onProgress);
    return existing.promise.finally(() => {
      if (onProgress) existing.listeners.delete(onProgress);
    });
  }

  const listeners = new Set();
  if (onProgress) listeners.add(onProgress);

  const promise = loadPipelineFn(modelId, {
    device: WHISPER_DEVICE,
    dtype: WHISPER_DTYPE,
    progress_callback: (ev) => {
      listeners.forEach((fn) => {
        try {
          fn(ev);
        } catch {
          /* listener errors must not fail the load */
        }
      });
    },
  })
    .then((pipeline) => {
      const entry = cache.get(modelId);
      if (entry) {
        entry.pipeline = pipeline;
        entry.promise = null;
        entry.listeners.clear();
      } else {
        cache.set(modelId, { pipeline, promise: null, listeners: new Set() });
      }
      return pipeline;
    })
    .catch((err) => {
      cache.delete(modelId);
      throw err;
    });

  cache.set(modelId, { pipeline: null, promise, listeners });
  return promise;
}

/** Drop in-memory entries. Does not dispose WASM; used by tests. */
export function resetWhisperPipelineCache() {
  cache.clear();
}

/**
 * @param {typeof loadTransformersPipeline|null} fn
 */
export function setWhisperPipelineLoader(fn) {
  loadPipelineFn = fn || loadTransformersPipeline;
}
